import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/client';

const fmt = n => Number(n || 0).toLocaleString('tr-TR');

const LEVEL_CFG = {
  critical: { label:'Kritik',  color:'var(--red)',    bg:'var(--red-dim)',    border:'rgba(239,68,68,.25)',   icon:'ti-alert-octagon'   },
  warning:  { label:'Uyarı',   color:'var(--yellow)', bg:'var(--yellow-dim)', border:'rgba(245,158,11,.25)',  icon:'ti-alert-triangle'  },
  info:     { label:'Bilgi',   color:'var(--accent2)',bg:'var(--accent-dim)', border:'rgba(59,130,246,.25)',  icon:'ti-info-circle'     },
};

const TYPE_LABELS = {
  device_offline:  'Bağlantı Kesildi',
  long_inactivity: 'Uzun Süredir Aktivite Yok',
  low_activity:    'Düşük Aktivite',
  anomaly_drop:    'Anormal Düşüş',
  recovery:        'Bağlantı Yeniden Kuruldu',
};

const STATUS_CFG = {
  active:   { label:'Aktif',    color:'var(--red)',    bg:'var(--red-dim)'    },
  resolved: { label:'Çözüldü', color:'var(--green)',  bg:'var(--green-dim)'  },
};

const KCard = ({ label, value, color = 'var(--text)', loading }) => (
  <div className="card" style={{ padding:'13px 15px', minWidth:0 }}>
    <div style={{ fontSize:10, textTransform:'uppercase', letterSpacing:'.06em', color:'var(--text3)', marginBottom:5 }}>{label}</div>
    {loading
      ? <div style={{ height:26, background:'var(--border)', borderRadius:4, animation:'pulse 1.2s ease infinite' }} />
      : <div style={{ fontSize:22, fontWeight:500, fontFamily:'var(--font-display)', color, lineHeight:1 }}>{value ?? '—'}</div>}
  </div>
);

const timeAgo = ts => {
  if (!ts) return '';
  const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (diff < 60)   return `${diff} sn önce`;
  if (diff < 3600) return `${Math.floor(diff/60)} dk önce`;
  if (diff < 86400) return `${Math.floor(diff/3600)} sa önce`;
  return `${Math.floor(diff/86400)} gün önce`;
};

export default function Alarms() {
  const navigate = useNavigate();

  const [alarms,  setAlarms]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter,  setFilter]  = useState('all'); // all | critical | warning | info | resolved

  const loadAlarms = useCallback(async () => {
    setLoading(true);
    try {
      const { data: r } = await api.get('/reports/alerts');
      setAlarms(r.data?.alarms || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAlarms(); }, [loadAlarms]);

  /* Filtre uygula */
  const filtered = alarms.filter(a => {
    if (filter === 'all')      return a.status !== 'resolved';
    if (filter === 'resolved') return a.status === 'resolved';
    return a.level === filter && a.status !== 'resolved';
  });

  /* Özet sayılar */
  const activeAlarms   = alarms.filter(a => a.status !== 'resolved');
  const criticalCount  = activeAlarms.filter(a => a.level === 'critical').length;
  const warningCount   = activeAlarms.filter(a => a.level === 'warning').length;
  const resolvedToday  = alarms.filter(a => {
    if (a.status !== 'resolved') return false;
    const d = new Date(a.resolved_at || a.updated_at);
    const now = new Date();
    return d.getFullYear()===now.getFullYear() && d.getMonth()===now.getMonth() && d.getDate()===now.getDate();
  }).length;

  const FILTER_TABS = [
    { key:'all',      label:`Aktif (${activeAlarms.length})`  },
    { key:'critical', label:`Kritik (${criticalCount})`        },
    { key:'warning',  label:`Uyarı (${warningCount})`          },
    { key:'info',     label:'Bilgi'                            },
    { key:'resolved', label:'Çözüldü'                          },
  ];

  return (
    <div style={{ padding:'var(--content-padding)', maxWidth:'var(--content-max)', margin:'0 auto' }}>

      {/* Başlık */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:18, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ fontSize:18, fontWeight:500, marginBottom:3 }}>Alarm Merkezi</h1>
          <p style={{ fontSize:12, color:'var(--text3)' }}>
            {activeAlarms.length > 0
              ? <span style={{ color:'var(--red)', fontWeight:600 }}>{activeAlarms.length} aktif alarm</span>
              : <span style={{ color:'var(--green)', fontWeight:600 }}>Aktif alarm yok</span>}
          </p>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={loadAlarms} disabled={loading}>
          <i className="ti ti-refresh" style={{ fontSize:13 }} aria-hidden="true" />
          {loading ? 'Yükleniyor...' : 'Yenile'}
        </button>
      </div>

      {/* 4 KPI kart */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,minmax(0,1fr))', gap:10, marginBottom:20 }}>
        <KCard label="Aktif Alarm"     value={activeAlarms.length} color={activeAlarms.length>0?'var(--red)':'var(--green)'} loading={loading} />
        <KCard label="Kritik"          value={criticalCount}       color={criticalCount>0?'var(--red)':'var(--text3)'}       loading={loading} />
        <KCard label="Bugün Çözülen"   value={resolvedToday}       color="var(--green)"                                      loading={loading} />
        <KCard label="Toplam Kayıt"    value={alarms.length}                                                                 loading={loading} />
      </div>

      {/* Filtre sekmeleri */}
      <div style={{ display:'flex', gap:4, marginBottom:16, background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--r-sm)', padding:3, width:'fit-content', flexWrap:'wrap' }}>
        {FILTER_TABS.map(t => (
          <button key={t.key} onClick={() => setFilter(t.key)}
            style={{ padding:'5px 14px', borderRadius:5, border:'none', fontSize:12.5, fontWeight:500, cursor:'pointer',
              background: filter===t.key ? 'var(--accent)' : 'transparent',
              color:      filter===t.key ? '#fff' : 'var(--text2)' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Alarm listesi */}
      {loading && (
        <div style={{ padding:40, textAlign:'center' }}><span className="spinner" /></div>
      )}

      {!loading && filtered.length === 0 && (
        <div style={{ padding:40, textAlign:'center', background:'var(--surface)', borderRadius:'var(--r)', border:'1px solid var(--border)', color:'var(--text3)' }}>
          <i className="ti ti-circle-check" style={{ fontSize:28, color:'var(--green)', display:'block', marginBottom:8 }} aria-hidden="true" />
          Bu kategoride alarm yok
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
          {filtered.map((alarm, i) => {
            const lvl = LEVEL_CFG[alarm.level] || LEVEL_CFG.info;
            const st  = STATUS_CFG[alarm.status] || STATUS_CFG.active;
            return (
              <div key={alarm.id || i} style={{
                display:'flex', alignItems:'center', gap:12, padding:'11px 16px',
                background: alarm.status==='resolved' ? 'var(--bg3)' : lvl.bg,
                border: `1px solid ${alarm.status==='resolved' ? 'var(--border)' : lvl.border}`,
                borderRadius:'var(--r)',
                opacity: alarm.status==='resolved' ? 0.7 : 1,
              }}>
                {/* Seviye ikonu */}
                <i className={`ti ${lvl.icon}`} style={{ fontSize:18, color:alarm.status==='resolved'?'var(--text3)':lvl.color, flexShrink:0 }} aria-hidden="true" />

                {/* Ana bilgi */}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:500, color:'var(--text)', marginBottom:2 }}>
                    {TYPE_LABELS[alarm.alert_type] || alarm.alert_type}
                  </div>
                  <div style={{ fontSize:11, color:'var(--text3)' }}>
                    {alarm.device_key || alarm.device_name || '—'}
                    {alarm.message && <span style={{ marginLeft:8 }}>{alarm.message.slice(0, 80)}</span>}
                  </div>
                </div>

                {/* Seviye badge */}
                <span style={{ fontSize:10, padding:'2px 8px', borderRadius:10, background:alarm.status==='resolved'?'var(--bg2)':lvl.bg, color:alarm.status==='resolved'?'var(--text3)':lvl.color, fontWeight:600, flexShrink:0 }}>
                  {lvl.label}
                </span>

                {/* Durum badge */}
                <span style={{ fontSize:10, padding:'2px 8px', borderRadius:10, background:st.bg, color:st.color, fontWeight:600, flexShrink:0 }}>
                  {st.label}
                </span>

                {/* Zaman */}
                <span style={{ fontSize:11, color:'var(--text3)', flexShrink:0, minWidth:70, textAlign:'right' }}>
                  {timeAgo(alarm.created_at)}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Tablo görünümü (resolved için) */}
      {!loading && filter === 'resolved' && filtered.length > 0 && (
        <div className="card" style={{ marginTop:16, padding:'16px 18px' }}>
          <h3 style={{ fontSize:14, marginBottom:12 }}>Geçmiş Tablo</h3>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Tip</th>
                  <th>Cihaz</th>
                  <th>Seviye</th>
                  <th>Oluşturuldu</th>
                  <th>Çözüldü</th>
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, 50).map((a, i) => {
                  const lvl = LEVEL_CFG[a.level] || LEVEL_CFG.info;
                  return (
                    <tr key={a.id || i}>
                      <td>{TYPE_LABELS[a.alert_type] || a.alert_type}</td>
                      <td style={{ color:'var(--text2)' }}>{a.device_key || a.device_name || '—'}</td>
                      <td>
                        <span style={{ fontSize:10, padding:'2px 8px', borderRadius:10, background:lvl.bg, color:lvl.color, fontWeight:600 }}>
                          {lvl.label}
                        </span>
                      </td>
                      <td style={{ color:'var(--text3)', fontSize:12 }}>{timeAgo(a.created_at)}</td>
                      <td style={{ color:'var(--green)', fontSize:12 }}>{timeAgo(a.resolved_at || a.updated_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
